import React from 'react';
import { MonitorPosition } from '../../../types/monitor.types';
import RProgressBar from './RProgressBar';
import DcaProgressBar from './DcaProgressBar';

interface PositionCardProps {
  position: MonitorPosition;
}

const PositionCard: React.FC<PositionCardProps> = ({ position }) => {
  const isBuy = position.direction === 'BUY';
  const isProfit = position.currentPL >= 0;

  const isDca = position.dcaStatus != null;
  const isDcaBuilding = isDca && position.dcaStatus === 'BUILDING';

  const tierLabel = position.tier === 0 ? 'No Tier' : position.tier === 1 ? 'Tier 1' : `Tier ${position.tier}`;
  const tierClass = position.tier === 0 ? 'tier-none' : position.tier === 1 ? 'tier-1' : 'tier-2';

  return (
    <div className={`position-card ${isProfit ? 'card-profit' : 'card-loss'}`}>
      <div className="card-header">
        <div className="card-model">
          {position.model}
          {isDca && (
            <span className="dca-entries-badge">
              {position.dcaEntriesFilled}/{position.dcaMaxEntries}
            </span>
          )}
        </div>
        <div className="card-header-right">
          {isDca && (
            <span className={`dca-status-badge ${isDcaBuilding ? 'dca-building' : 'dca-active'}`}>
              {position.dcaStatus}
            </span>
          )}
          <span className={`direction-badge ${isBuy ? 'dir-buy' : 'dir-sell'}`}>
            {position.direction}
          </span>
        </div>
      </div>

      <div className="card-instrument">{position.instrument}</div>

      <div className="card-prices">
        <div className="price-row">
          <span className="price-label">Entry</span>
          <span className="price-value">{position.entryPrice.toFixed(position.entryPrice < 10 ? 5 : 2)}</span>
        </div>
        <div className="price-row">
          <span className="price-label">Current</span>
          <span className="price-value">{position.currentPrice.toFixed(position.currentPrice < 10 ? 5 : 2)}</span>
        </div>
      </div>

      <div className="card-pl-row">
        <span className={`card-pl ${isProfit ? 'pl-positive' : 'pl-negative'}`}>
          ${position.currentPL.toFixed(2)}
        </span>
        <span className="card-r-value">({position.currentR.toFixed(2)}R)</span>
      </div>

      <RProgressBar currentR={position.currentR} tier={position.tier} tier1ThresholdR={position.tier1ThresholdR} exitMode={position.exitMode} takeProfitR={position.takeProfitR} />
      {isDcaBuilding && (
        <DcaProgressBar
          dcaDropR={position.dcaDropR ?? 0}
          dcaNextTriggerR={position.dcaNextTriggerR ?? null}
          dcaHardStopR={position.dcaHardStopR ?? 1.5}
          entriesFilled={position.dcaEntriesFilled ?? 1}
        />
      )}

      <div className="card-details">
        <div className="detail-row">
          <span className={`tier-badge ${tierClass}`}>{tierLabel}</span>
          <span className="detail-value">R = ${position.rValue.toFixed(0)}</span>
        </div>
        {isDcaBuilding && position.dcaStopProximityR != null && (
          <div className="detail-row">
            <span className="detail-label">To Stop</span>
            <span className="detail-value dca-stop-proximity">{position.dcaStopProximityR.toFixed(2)}R</span>
          </div>
        )}
        {position.protectedProfit > 0 && (
          <div className="detail-row">
            <span className="detail-label">Protected</span>
            <span className="detail-value protected-value">${position.protectedProfit.toFixed(2)}</span>
          </div>
        )}
        {position.stopLevel > 0 && (
          <div className="detail-row">
            <span className="detail-label">Stop</span>
            <span className="detail-value">{position.stopLevel.toFixed(position.stopLevel < 10 ? 5 : 2)}</span>
          </div>
        )}
        <div className="detail-row">
          <span className="detail-label">Size</span>
          <span className="detail-value">{position.size}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Duration</span>
          <span className="detail-value">{position.duration}</span>
        </div>
      </div>
    </div>
  );
};

export default PositionCard;
